/**
 * Reviews Service
 * Handles API calls for reviews management
 */

import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { api } from '../utils/apiClient';
import type { GetReviewsResponse, ContactResponseRecord, ReviewData } from '../types/review';

/**
 * Transform API contact response record to ReviewData
 */
const transformRecord = (record: ContactResponseRecord): ReviewData => {
  return {
    id: record.contact_id,
    customer_name: record.customer_name,
    customer_email: record.customer_email,
    customer_phone: record.customer_phone,
    campaign_id: record.campaign_id,
    campaign_name: record.campaign_name,
    platform: record.platform,
    platform_link: record.platform_link,
    review_text: record.review_text,
    review_url: record.review_url,
    posted_at: record.review_posted_at,
    survey_completed: record.survey_completed,
    survey_completed_at: record.survey_completed_at,
    review_posted: record.review_posted,
    reward_type: record.reward_type,
    reward_amount: record.reward_amount,
    reward_value: record.reward_amount, // Same as reward_amount for display
    reward_claim_status: record.reward_claimed 
      ? (record.reward_claim_status || 'pending')
      : 'not_claimed',
    payment_method: record.customer_contact_method,
    coupon_code: record.coupon_code,
    created_at: record.contact_created_at,
    updated_at: record.last_updated_at,
  };
};

/**
 * Get all contact responses (reviews) for the authenticated user's campaigns
 * Requires authentication (token automatically included)
 * Uses the new endpoint: /api/v1/contacts/responses?limit=100&offset=0&survey_completed=true
 */
export const getReviews = async (
  limit: number = 100,
  offset: number = 0
): Promise<{ success: boolean; data?: { reviews: ReviewData[]; total: number }; message?: string; error?: string }> => {
  try {
    const response = await api.get<GetReviewsResponse>(
      `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CONTACT.RESPONSES}?limit=${limit}&offset=${offset}&survey_completed=true`
    );
    
    if (response.success && response.data?.records) {
      // Transform API records to ReviewData
      const reviews = response.data.records.map(transformRecord);
      
      return {
        success: true,
        data: {
          reviews,
          total: response.data.total_records,
        },
      };
    }
    
    return response as any;
  } catch (error) {
    console.error('❌ Error fetching reviews:', error);
    throw error;
  }
};

/**
 * Export reviews data as CSV
 * Requires authentication (token automatically included)
 */
export const exportReviews = async (): Promise<Blob> => {
  try {
    const response = await fetch(
      `${API_BASE_URL.PROFILE}/contacts/responses/export`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to export reviews');
    }

    const blob = await response.blob();
    console.log('✅ Reviews exported');
    return blob;
  } catch (error) {
    console.error('❌ Error exporting reviews:', error);
    throw error;
  }
};

/**
 * Monthly Review Data Types
 */
export interface MonthlyReviewData {
  month: string;
  month_name: string;
  total_reviews: number;
  average_rating: number;
}

export interface GetMonthlyReviewsResponse {
  success: boolean;
  data?: {
    months: MonthlyReviewData[];
    total: number;
  };
  message?: string;
  error?: string;
}

/**
 * Get monthly reviews statistics
 * Requires authentication (token automatically included)
 */
export const getMonthlyReviews = async (): Promise<GetMonthlyReviewsResponse> => {
  try {
    const response = await api.get<GetMonthlyReviewsResponse>(
      `${API_BASE_URL.PROFILE}${API_ENDPOINTS.CONTACT.REVIEWS_MONTHLY}`
    );
    
    return response;
  } catch (error) {
    console.error('❌ Error fetching monthly reviews:', error);
    throw error;
  }
};

